<script setup>
import Jumbo from '@/components/Docs/Jumbo.vue'
import DocSection from '@/components/Docs/Section.vue'
import CodeBlock from '@/components/Docs/CodeBlock.vue'
import JsonBlock from '@/components/Docs/JsonBlock.vue'
  
</script>

<template>
  <div class="wrap row">
    <jumbo title="Feeds">
Webscouts feeds provide organizations with valuable information and insights into emerging cyber threats, vulnerabilities, and malicious activities. These feeds aggregate data from various of our sources, including security researchers, vendors, and global threat intelligence networks, to deliver timely and actionable intelligence to security teams. 
<br><br>
Each feed is identified by a unique uuid, which is used interacting with the feed. All our feeds can be on the <router-link to="/feeds">feeds page</router-link>, which also shows each feed's schema (the output format of it’s entries) and it’s uuid (id).
    </jumbo>

    <doc-section title="List feeds">
      <template v-slot:left>
        A list of all public/featured feeds can be obtained by accessing the /feeds/featured URL. Besides returning the general details and information about each feed, they also return the ID which should be used when listing the data/entries of each respective feed.

      </template>

      <template v-slot:right>
        <code-block title="/feeds/featured" method="GET">
          curl -X GET https://api.webscout.io/feeds/featured
        </code-block>

        <json-block title="Response">
{"data":[{"Id":"0bf81469-1eb4-44c8-b325-1426d15ccb18","Owner":"webscout_test","OwnerId":"da33ed10-dc24-44f9-873c-1c3e28e6314f","Name":"Catphish","Description":"This score is derived from our AI model which is based on Gradient Boosting. We continuously update this model with data on new phishing kits, frameworks, and phishing websites we find in the wild. The model is biased towards generating more false positives than false negatives. This means it is more likely to incorrectly label legitimate login pages as malicious rather than erroneously identifying malicious sites as safe.\n\nThe score is still highly experimental and should not be regarded as a source of truth. A low Phishing Probability score does NOT definitively confirm the safety of the URL you have searched.","ItemCount":137956,"LastUpdated":"2024-03-14T11:07:38.432994Z","Private":0,"Featured":1,"Category":"Phishing","Schema":null,"Summary":"Catphish is an experimental phishing feed"},{"Id":"76cfa186-1cfe-4a5a-9bac-41645e1f3f6d","Owner":"webscout_test","OwnerId":"da33ed10-dc24-44f9-873c-1c3e28e6314f","Name":"Newly Registered Domains","Description":"The registration of domains is a completely normal part of operating and creating new web resources. As such most domains within this feed are legitimate. It can however be especially advantageous for security researchers to keep an eye on such resources, as these might present future attack vectors, or possibly represent infrastructure of adversaries.\n\nEvery domain webscout sees, which is **no older than 7 days**, will be added to this feed.","ItemCount":70385,"LastUpdated":"2024-03-13T08:52:32.507808Z","Private":0,"Featured":1,"Category":"Misc","Schema":null,"Summary":"This feed contains a summary of the newly registered domains we find throughout our scanning and monitoring of the web"}],"status":"success"}
        </json-block>
      </template>
    </doc-section>

    <doc-section title="Get feed information">
      <template v-slot:left>
        A feed's information can be listed by accessing the URL /feeds/:id supplying the id of the feed. Occasionally the feed’s description will be in markdown. <br><br>
The feed information displays the schema of the feed. The schema shows the format that the individual entries of the feed follows, and how they are returned when listing the feeds data.

      </template>

      <template v-slot:right>
        <code-block title="/feeds/:id" method="GET">
          curl -X GET https://api.webscout.io/feeds/&lt;FEED_ID&gt;
        </code-block>

        <json-block title="Response">
        {"data":{"Id":"0bf81469-1eb4-44c8-b325-1426d15ccb18","Owner":"webscout_test","OwnerId":"da33ed10-dc24-44f9-873c-1c3e28e6314f","Name":"Catphish","Description":"This score is derived from our AI model which is based on Gradient Boosting. We continuously update this model with data on new phishing kits, frameworks, and phishing websites we find in the wild. The model is biased towards generating more false positives than false negatives. This means it is more likely to incorrectly label legitimate login pages as malicious rather than erroneously identifying malicious sites as safe.\n\nThe score is still highly experimental and should not be regarded as a source of truth. A low Phishing Probability score does NOT definitively confirm the safety of the URL you have searched.","ItemCount":137956,"LastUpdated":"2024-03-14T11:07:38.432994Z","Private":0,"Featured":0,"Category":"","Schema":{"FinalUrl":"https://onemoremarket.com/","Score":73.512405,"StartUrl":"https://onemoremarket.com/","Verdict":1},"Summary":"Catphish is an experimental phishing feed"},"status":"success"}
        </json-block>
      </template>
    </doc-section>

    <doc-section title="Get feed data">
      <template v-slot:left>
        The feed data endpoint will return the latest 200 items from the feed. The structure of the individual items follows the feeds schema definition. Which can be found by listing the feed information. <br><br>
To always stay up to date with the most recent data, it is recommended to continuously query the endpoint
      </template>

      <template v-slot:right>
        <code-block title="/feeds/:id/data"  method="GET">
            curl -X GET https://api.webscout.io/feeds/&lt;FEED_ID&gt;/data?apikey=&lt;APIKEY&gt;
        </code-block>

        <json-block title="Response">
{
    "data": [
        {
            "FinalUrl": "https://xxe.qfc.mybluehost.me/usps/verification/",
            "Score": 91.827736,
            "StartUrl": "https://xxe.qfc.mybluehost.me/usps/verification/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://pos.axiomwallet.com/admin/",
            "Score": 93.56857,
            "StartUrl": "https://pos.axiomwallet.com/admin/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://demo-integration.authento.io/",
            "Score": 98.80033,
            "StartUrl": "https://demo-integration.authento.io/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://contrato-digital.site/",
            "Score": 98.613335,
            "StartUrl": "https://contrato-digital.site",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://emp.contrato-digital.site/",
            "Score": 98.76478,
            "StartUrl": "https://emp.contrato-digital.site",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://www.reliance-cr.com/",
            "Score": 95.27572,
            "StartUrl": "https://www.reliance-cr.com/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://marissakmerrill.com/",
            "Score": 92.730095,
            "StartUrl": "https://marissakmerrill.com/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://course.alenanoga.ru/login?redirectTo=https://course.alenanoga.ru/",
            "Score": 95.40621,
            "StartUrl": "https://course.alenanoga.ru/login?redirectTo=https://course.alenanoga.ru/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://www.cagacrypto.com/",
            "Score": 97.629845,
            "StartUrl": "https://www.cagacrypto.com/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://chat.z-l.top/",
            "Score": 98.43895,
            "StartUrl": "https://chat.z-l.top/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://chat.kakaocopy.com/app/login",
            "Score": 99.07343,
            "StartUrl": "https://chat.kakaocopy.com/app/login",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://wwwrelyingpafrty-okta-stabple-evnail-a8utoscoastwyde.tiny.us/",
            "Score": 90.99864,
            "StartUrl": "https://wwwrelyingpafrty-okta-stabple-evnail-a8utoscoastwyde.tiny.us/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://www.thetech.buzz/p/deals-and-jobs?utm_source=www.thetech.buzz",
            "Score": 95.08018,
            "StartUrl": "https://www.thetech.buzz/p/deals-and-jobs?utm_source=www.thetech.buzz",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://telstra-102119.weeblysite.com/",
            "Score": 97.45526,
            "StartUrl": "https://telstra-102119.weeblysite.com/",
            "Verdict": 0
        },
        {
            "FinalUrl": "https://www.ariston-hotpoint-service.center/",
            "Score": 96.1871,
            "StartUrl": "https://www.ariston-hotpoint-service.center/",
            "Verdict": 0
        }
    ],
    "status": "success"
}
        </json-block>
      </template>
    </doc-section>


  </div>
</template>

<style scoped>
.wrap {
  height: 100%;
  overflow-y: scroll;
}

.wrap > * {
  margin-top: 2.5rem;
}
.wrap > *:not(:first-child) {
  border-top: 2px solid #1e252f;
  padding-top: 2.5rem;
}
</style>
